import CheckIcon from "@mui/icons-material/Check";
import { type FC, Fragment, type Ref } from "react";

import { nameByPlatform } from "~/constants/platfroms";
import type { IMarketDropdownGroup } from "~/feature/chart/components/MarketsDropdown/components/MarketsDropdownMenu/buildDropdownGroups";
import { colorByPlatform } from "~/feature/chart/constants";

import { PlatformDots } from "./components/PlatformDots";
import { CONSENSUS_OPTION_LABEL } from "./constants";
import * as Styled from "./MarketGroupsMenu.styles";

interface IMarketGroupsMenuProps {
  ref?: Ref<HTMLDivElement>;
  groups: IMarketDropdownGroup[];
  selectedIds: string[];
  isConsensusSelected: (group: IMarketDropdownGroup) => boolean;
  banner?: string;
  onGroupClick: (group: IMarketDropdownGroup) => void;
  onMarketClick: (group: IMarketDropdownGroup, marketId: string) => void;
  onConsensusClick: (group: IMarketDropdownGroup) => void;
}

export const MarketGroupsMenu: FC<IMarketGroupsMenuProps> = ({
  ref,
  groups,
  selectedIds,
  isConsensusSelected,
  banner,
  onGroupClick,
  onMarketClick,
  onConsensusClick,
}) => (
  <Styled.Root ref={ref}>
    {banner && <Styled.CapBanner>{banner}</Styled.CapBanner>}
    {groups.map((group) => {
      const consensusSelected = isConsensusSelected(group);

      return (
        <Fragment key={group.id}>
          <Styled.GroupHeader
            $selected={group.isSelected}
            data-selected={group.isSelected || undefined}
            onClick={() => onGroupClick(group)}
          >
            <Styled.GroupLabel title={group.title}>
              {group.title}
            </Styled.GroupLabel>
            <Styled.HeaderRight>
              <PlatformDots markets={group.markets} />
              {group.isExpandable ? (
                <Styled.ArrowIcon $open={group.isExpanded} />
              ) : (
                group.isSelected && <Styled.SelectedIcon />
              )}
            </Styled.HeaderRight>
          </Styled.GroupHeader>

          {group.isExpanded && (
            <Styled.GroupBody>
              {group.markets.map(({ id, platform }) => {
                const selected = selectedIds.includes(id);

                return (
                  <Styled.PlatformRow
                    key={id}
                    $selected={selected}
                    onClick={() => onMarketClick(group, id)}
                  >
                    <Styled.PlatformDot $color={colorByPlatform[platform]} />
                    <Styled.PlatformLabel>
                      {nameByPlatform[platform]}
                    </Styled.PlatformLabel>
                    {selected && (
                      <Styled.PlatformCheck>
                        <CheckIcon />
                      </Styled.PlatformCheck>
                    )}
                  </Styled.PlatformRow>
                );
              })}

              {group.markets.length > 1 && (
                <Styled.ConsensusOption
                  $selected={consensusSelected}
                  onClick={() => onConsensusClick(group)}
                >
                  {CONSENSUS_OPTION_LABEL}
                  {consensusSelected && (
                    <Styled.ConsensusCheck>
                      <CheckIcon />
                    </Styled.ConsensusCheck>
                  )}
                </Styled.ConsensusOption>
              )}
            </Styled.GroupBody>
          )}
        </Fragment>
      );
    })}
  </Styled.Root>
);
